import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
    onDocumentLoaded(() => {
      // Initialize after a short delay to ensure DOM is fully rendered
      setTimeout(() => {
        this.#initTextModeImageHover();
      }, 100);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Initialize text mode image hover functionality
   */
  #initTextModeImageHover() {
    // Find all text mode image containers (there may be multiple menus)
    const imageContainers = document.querySelectorAll('[data-text-menu-image-container]');
    
    if (imageContainers.length === 0) {
      return;
    }

    imageContainers.forEach((imageContainer) => {
      const containerElement = /** @type {HTMLElement} */ (imageContainer);
      const imageWrapper = /** @type {HTMLElement | null} */ (containerElement.querySelector('.mega-menu__text-image-wrapper'));
      const defaultImage = imageWrapper?.dataset.textMenuDefaultImage;
      const defaultAlt = imageWrapper?.dataset.textMenuDefaultAlt;
      const defaultTitle = imageWrapper?.dataset.textMenuDefaultTitle;
      const collectionNameElement = /** @type {HTMLElement | null} */ (containerElement.querySelector('[data-text-menu-collection-name]'));
      
      // Create a function to get the current image element (always fresh reference)
      const getImageElement = () => {
        return /** @type {HTMLImageElement | null} */ (imageWrapper?.querySelector('.mega-menu__text-image'));
      };
      
      let imageElement = getImageElement();

      if (!imageWrapper || !imageElement) {
        return;
      }

      // Find the menu grid that contains this image container
      const menuGrid = containerElement.closest('[data-menu-grid-id]');
      
      if (!menuGrid) {
        return;
      }

      // Use event delegation on the menu grid for better reliability
      // This handles dynamically added elements and ensures events are captured
      const handlePointerEnter = (/** @type {PointerEvent} */ event) => {
        const eventTarget = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
        if (!eventTarget) {
          return;
        }

        // Try to find the link - first check if target is itself a link
        let target = null;
        if (eventTarget instanceof HTMLAnchorElement && eventTarget.hasAttribute('data-collection-image')) {
          target = eventTarget;
        } else {
          // Try to find parent link
          target = /** @type {HTMLElement | null} */ (eventTarget.closest('a[data-collection-image]'));
        }
        
        // Only process if we found a valid link and it's different from the current one
        if (!target || !menuGrid.contains(target)) {
          return;
        }

        // Skip if we're already hovering over this link
        if (currentHoveredLink === target) {
          return;
        }

        currentHoveredLink = target;

        const imageUrl = target.dataset.collectionImage;
        const imageAlt = target.dataset.collectionImageAlt || '';
        const collectionTitle = target.dataset.collectionTitle || target.textContent?.trim() || '';

        if (imageUrl && imageElement) {
          // Normalize URL to absolute URL for comparison
          const normalizeUrl = (/** @type {string} */ url) => {
            if (!url) return '';
            try {
              if (url.startsWith('http://') || url.startsWith('https://')) {
                return url;
              }
              return new URL(url, window.location.origin).href;
            } catch (e) {
              return url;
            }
          };
          
          const normalizedImageUrl = normalizeUrl(imageUrl);
          const normalizedCurrentSrc = normalizeUrl(imageElement.src);
          
          if (normalizedCurrentSrc === normalizedImageUrl) {
            return;
          }
          
          // Always get fresh reference to image element
          imageElement = getImageElement();
          if (!imageElement) {
            return;
          }
          
          // Clear srcset first, then src
          if (imageElement.srcset) {
            imageElement.removeAttribute('srcset');
          }
          imageElement.src = '';
          if (imageElement.sizes) {
            imageElement.removeAttribute('sizes');
          }
          
          requestAnimationFrame(() => {
            // Get fresh reference again
            imageElement = getImageElement();
            if (!imageElement) {
              return;
            }
            
            imageElement.src = normalizedImageUrl;
            
            // Ensure srcset is still cleared
            if (imageElement.srcset) {
              imageElement.removeAttribute('srcset');
            }
            
            if (imageAlt) {
              imageElement.alt = imageAlt;
            }
            
            // Update collection name
            if (collectionNameElement && collectionTitle) {
              collectionNameElement.textContent = collectionTitle;
            }
          });
        }
      };

      const handlePointerLeave = (/** @type {PointerEvent} */ event) => {
        const eventTarget = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
        const target = /** @type {HTMLElement | null} */ (eventTarget?.closest('a[data-collection-image]'));
        if (!target || !menuGrid.contains(target)) {
          return;
        }

        // Only restore default if we're leaving the menu grid entirely
        const relatedTarget = /** @type {HTMLElement | null} */ (event.relatedTarget instanceof Element ? event.relatedTarget : null);
        const isLeavingGrid = !menuGrid.contains(relatedTarget);
        
        if (isLeavingGrid) {
          imageElement = getImageElement();
          if (defaultImage && imageElement && imageElement.src !== defaultImage) {
            imageElement.src = defaultImage;
            if (defaultAlt) {
              imageElement.alt = defaultAlt;
            }
            // Restore default collection name
            if (collectionNameElement && defaultTitle) {
              collectionNameElement.textContent = defaultTitle;
            }
          }
        }
      };

      // Track current hovered link to avoid duplicate switches
      let currentHoveredLink = /** @type {HTMLElement | null} */ (null);

      const handleMouseEnter = (/** @type {MouseEvent} */ event) => {
        const eventTarget = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
        if (!eventTarget) {
          return;
        }

        // Try to find the link - first check if target is itself a link
        let target = null;
        if (eventTarget instanceof HTMLAnchorElement && eventTarget.hasAttribute('data-collection-image')) {
          target = eventTarget;
        } else {
          // Try to find parent link
          target = /** @type {HTMLElement | null} */ (eventTarget.closest('a[data-collection-image]'));
        }
        
        // Only process if we found a valid link and it's different from the current one
        if (!target || !menuGrid.contains(target)) {
          return;
        }

        // Skip if we're already hovering over this link
        if (currentHoveredLink === target) {
          return;
        }

        currentHoveredLink = target;

        const imageUrl = target.dataset.collectionImage;
        const imageAlt = target.dataset.collectionImageAlt || '';
        const collectionTitle = target.dataset.collectionTitle || target.textContent?.trim() || '';

        if (imageUrl && imageElement) {
          // Normalize URL to absolute URL for comparison
          const normalizeUrl = (/** @type {string} */ url) => {
            if (!url) return '';
            try {
              if (url.startsWith('http://') || url.startsWith('https://')) {
                return url;
              }
              return new URL(url, window.location.origin).href;
            } catch (e) {
              return url;
            }
          };
          
          const normalizedImageUrl = normalizeUrl(imageUrl);
          const normalizedCurrentSrc = normalizeUrl(imageElement.src);
          
          if (normalizedCurrentSrc === normalizedImageUrl) {
            return;
          }
          
          // Always get fresh reference to image element
          imageElement = getImageElement();
          if (!imageElement) {
            return;
          }
          
          // CRITICAL: Clear srcset FIRST, then src
          // Browser prioritizes srcset over src, so we must clear srcset first
          if (imageElement.srcset) {
            imageElement.removeAttribute('srcset');
          }
          imageElement.src = '';
          
          // Also clear sizes attribute if it exists
          if (imageElement.sizes) {
            imageElement.removeAttribute('sizes');
          }
          
          // Force a reflow to ensure the attributes are cleared
          void imageElement.offsetWidth;
          
          // Use requestAnimationFrame to ensure the attributes are cleared before setting new one
          requestAnimationFrame(() => {
            // Always get fresh reference to image element
            imageElement = getImageElement();
            if (!imageElement) {
              return;
            }
            
            // Set new src (without srcset to ensure browser uses src)
            imageElement.src = normalizedImageUrl;
            
            // Double-check srcset is cleared
            if (imageElement.srcset) {
              imageElement.removeAttribute('srcset');
            }
            
            if (imageAlt) {
              imageElement.alt = imageAlt;
            }
            
            // Update collection name
            if (collectionNameElement && collectionTitle) {
              collectionNameElement.textContent = collectionTitle;
            }
          });
        }
      };

      const handleMouseLeave = (/** @type {MouseEvent} */ event) => {
        const relatedTarget = /** @type {HTMLElement | null} */ (event.relatedTarget);
        const isLeavingGrid = !menuGrid.contains(relatedTarget);
        
        // Only restore default if we're leaving the menu grid entirely
        if (isLeavingGrid) {
          currentHoveredLink = null;
          imageElement = getImageElement();
          if (defaultImage && imageElement && imageElement.src !== defaultImage) {
            imageElement.src = defaultImage;
            if (defaultAlt) {
              imageElement.alt = defaultAlt;
            }
            // Restore default collection name
            if (collectionNameElement && defaultTitle) {
              collectionNameElement.textContent = defaultTitle;
            }
          }
        } else {
          // Check if we're leaving the current hovered link
          const eventTarget = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
          if (eventTarget) {
            const leavingLink = eventTarget instanceof HTMLAnchorElement 
              ? eventTarget 
              : eventTarget.closest('a[data-collection-image]');
            if (leavingLink === currentHoveredLink) {
              // We're leaving the current link, but still in the menu grid
              // Don't clear currentHoveredLink yet, wait to see if we enter another link
              const enteringLink = relatedTarget instanceof Element
                ? (relatedTarget instanceof HTMLAnchorElement && relatedTarget.hasAttribute('data-collection-image')
                    ? relatedTarget
                    : relatedTarget.closest('a[data-collection-image]'))
                : null;
              if (!enteringLink || !menuGrid.contains(enteringLink)) {
                // Not entering another link, clear the current one
                currentHoveredLink = null;
              }
            }
          }
        }
      };

      // Use mouseover/pointerover instead of mouseenter/pointerenter because
      // mouseenter/pointerenter don't bubble, but mouseover/pointerover do
      menuGrid.addEventListener('pointerover', handlePointerEnter, { 
        signal: this.#abortController.signal
      });
      menuGrid.addEventListener('pointerout', handlePointerLeave, { 
        signal: this.#abortController.signal
      });
      menuGrid.addEventListener('mouseover', handleMouseEnter, { 
        signal: this.#abortController.signal
      });
      menuGrid.addEventListener('mouseout', handleMouseLeave, { 
        signal: this.#abortController.signal
      });
    });
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
